import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/useAuth";
import { globalKey } from "../query/queryKeys";

const PERSONS_QUERY_ROOT = "persons";

export type AggregatorProvider = "UrbanSportsClub" | "EgymWellpass";
type Identity = { provider: AggregatorProvider; customerId: string };
export async function invalidateAggregatorAvailability(cache: QueryClient) {
  await Promise.all([
    cache.invalidateQueries({ queryKey: globalKey("lookups", "aggregator-integrations") }),
    cache.invalidateQueries({ predicate: query => query.queryKey[0] === PERSONS_QUERY_ROOT && query.queryKey.includes("aggregator-requirements") })
  ]);
}
export type AggregatorRequirement = {
  integrationId: string; provider: AggregatorProvider; displayName: string;
  contractId: string | null; packageId: string | null; walletId: string | null;
  startDate: string; customerId: string | null;
};

export function usePersonAggregatorRequirements(personId: string, enabled = true) {
  const { api } = useAuth();
  return useQuery({ queryKey: globalKey("persons", personId, "aggregator-requirements"),
    queryFn: () => api.get<AggregatorRequirement[]>(`/api/v1/aggregators/persons/${personId}/requirements`), enabled });
}

export function usePersonAggregatorIdentities(personId: string) {
  const { api } = useAuth();
  const cache = useQueryClient();
  const queryKey = globalKey("persons", personId, "aggregator-identities");
  const invalidate = async () => {
    await Promise.all([
      cache.invalidateQueries({ queryKey }),
      cache.invalidateQueries({ queryKey: globalKey("persons", personId, "aggregator-requirements") }),
      cache.invalidateQueries({ queryKey: ["person", personId, "check-in-eligibility"] })
    ]);
  };
  const saveIdentity = useMutation({ mutationFn: ({ provider, customerId }: Identity) =>
    api.put(`/api/v1/aggregators/persons/${personId}/${provider}`, { provider, customerId: customerId.trim() }), onSuccess: invalidate });
  const linkWellpass = useMutation({ mutationFn: ({ integrationId, tan }: { integrationId: string; tan: string }) =>
    api.post(`/api/v1/aggregators/persons/${personId}/wellpass`, { integrationId, tan: tan.trim() }), onSuccess: invalidate });
  return { saveIdentity, linkWellpass };
}
